import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Input,
  Space,
  Typography,
} from 'antd';
import ProductStatusFilter from 'app/shared/components/ProductStatusFilter';
import type { ProductStatusFilterValue } from 'app/shared/components/productStatusFilterUtils';
import { matchesProductStatusFilter } from 'app/shared/components/productStatusFilterUtils';
import ManagerSearchByName from 'app/shared/components/ManagerSearchByName';
import { PlusOutlined } from '@ant-design/icons';

import { PublishedEventType, StatusIcon, useSignalREvent } from 'app/helpers/signalRHelper';
import { getSelectedDivisionId } from 'app/shared/division/selectedDivisionStorage';

import {
  CommissionCategory,
  fetchCommissionCategories,
  setFilter,
} from './actions';

import {
  getCommissionLoading,
  getFilteredCommissionList,
  getFilterName,
} from './selectors';

import './Page.css';

const { Title } = Typography;

interface StateProps {
  loading: boolean;
  filtered: CommissionCategory[];
  filterName: string;
}

interface DispatchProps {
  fetchList: () => void;
  setFilterAction: (value: string) => void;
}

type Props = StateProps & DispatchProps;

const CommissionManagerPage: React.FC<Props> = ({
  loading,
  filtered,
  filterName,
  fetchList,
  setFilterAction,
}) => {
  const navigate = useNavigate();
  const [activeStatusFilter, setActiveStatusFilter] = useState<ProductStatusFilterValue>('all');

  const displayed = useMemo(
    () => filtered.filter((item) => matchesProductStatusFilter(item.active, activeStatusFilter)),
    [filtered, activeStatusFilter]
  );

  const refreshList = useCallback(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    const onDivisionChanged = () => {
      refreshList();
    };
    window.addEventListener('selectedDivisionChanged', onDivisionChanged);
    return () => window.removeEventListener('selectedDivisionChanged', onDivisionChanged);
  }, [refreshList]);

  const divisionId = getSelectedDivisionId();
  const { status, lastEventReceived } = useSignalREvent(
    [PublishedEventType.CommissionGroupEventPublished],
    refreshList,
    divisionId
  );

  const handleAddNew = () => {
    navigate('/commission-category-manager/add');
  };

  const handleOpenBuilder = (record: CommissionCategory) => {
    navigate(`/commission-category-manager/${record.id}`);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      render: (id: number) => <span>{id || '-'}</span>,
      sorter: (a: CommissionCategory, b: CommissionCategory) => (a.id ?? 0) - (b.id ?? 0),
    },
    {
      title: 'Commission Name',
      dataIndex: 'name',
      width: 220,
      key: 'name',
      render: (_: unknown, record: CommissionCategory) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => handleOpenBuilder(record)}>
          {record.name}
        </Button>
      ),
      sorter: (a: CommissionCategory, b: CommissionCategory) => (a.name ?? '').localeCompare(b.name ?? ''),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 140,
      render: (text: string | undefined) => <span>{text || '-'}</span>,
    },
    {
      title: 'Last Modified By',
      dataIndex: 'lastModifiedBy',
      key: 'lastModifiedBy',
      width: 140,
      render: (text: string | undefined) => <span>{text || '-'}</span>,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      align: 'center' as const,
      width: 80,
      render: (val: boolean | undefined) =>
        val !== false ? <span className="commission-active-yes">Yes</span> : <span className="commission-active-no">No</span>,
    },
  ];

  return (
    <>
      <Title level={3} style={{ margin: 0, marginBottom: 16 }}>Commission Category Manager</Title>

      <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <Space>
          <ManagerSearchByName value={filterName} onChange={setFilterAction} />
          <ProductStatusFilter
            value={activeStatusFilter}
            onChange={setActiveStatusFilter}
          />
        </Space>

        <Space>
          <StatusIcon status={status} lastEventReceived={lastEventReceived} />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
            Add New
          </Button>
        </Space>
      </Space>

      <Table<CommissionCategory>
        rowKey="id"
        loading={loading}
        dataSource={displayed}
        columns={columns}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        rowClassName={(record) => (record.active === false ? 'inactive-row' : '')}
      />
    </>
  );
};

const mapStateToProps = (state: unknown): StateProps => ({
  loading: getCommissionLoading(state as object),
  filtered: getFilteredCommissionList(state as object),
  filterName: getFilterName(state as object),
});

const mapDispatchToProps: DispatchProps = {
  fetchList: fetchCommissionCategories,
  setFilterAction: setFilter,
};

export default connect(mapStateToProps, mapDispatchToProps)(CommissionManagerPage);